/**
 * Web session cart attributes:
 * - Stamp `_audience=web_authenticated` and `_authenticated=true` when logged in
 *   on the website (not mobile app)
 * - Clear `_audience`, `_authenticated`, and leftover `_channel` / `_source`
 *   when guest or on logout
 *
 * Mobile app sessions are owned by mobile-app-webview.js (`_audience=mobile_app`,
 * `_authenticated=true`).
 * Keeps Shopify.Cart.cart.attributes in sync after successful updates.
 * Uses keepalive so navigation is less likely to abort the write.
 */
;(function () {
  const AUDIENCE_KEY = "_audience"
  const AUTHENTICATED_KEY = "_authenticated"
  const CHANNEL_KEY = "_channel"
  const SOURCE_KEY = "_source"
  const WEB_AUTHENTICATED = "web_authenticated"
  const AUTHENTICATED_VALUE = "true"
  const SESSION_ATTR_KEYS = [
    AUDIENCE_KEY,
    AUTHENTICATED_KEY,
    CHANNEL_KEY,
    SOURCE_KEY,
  ]

  let stampInFlight = false
  let interactionBound = false

  function isMobileApp() {
    return !!window.Shopify?.Therabody?.MobileApp?.isMobileApp
  }

  function isLoggedIn() {
    return !!window.Shopify?.Customer?.isLoggedIn
  }

  function getCartAttribute(name) {
    const val = window.Shopify?.Cart?.cart?.attributes?.[name]
    if (val == null || val === "") return null
    return String(val)
  }

  function getCartAudience() {
    return getCartAttribute(AUDIENCE_KEY)
  }

  function isAuthenticatedAttrSet() {
    return getCartAttribute(AUTHENTICATED_KEY) === AUTHENTICATED_VALUE
  }

  function hasSessionAttrsToClear() {
    return SESSION_ATTR_KEYS.some(function (key) {
      return getCartAttribute(key) != null
    })
  }

  function updateLocalCartAttributes(attrs) {
    const Sc = window.Shopify?.Cart
    if (!Sc?.cart) return
    const next = Object.assign({}, Sc.cart.attributes)
    Object.keys(attrs).forEach(function (key) {
      const value = attrs[key]
      if (value == null || value === "") {
        delete next[key]
      } else {
        next[key] = value
      }
    })
    Sc.cart.attributes = next
  }

  async function postCartAttributes(attrs) {
    const updateUrl = window.Shopify?.routes?.cart_update_url
    if (!updateUrl) return false
    if (typeof fetchConfig !== "function") return false

    const res = await fetch(updateUrl, {
      ...fetchConfig(),
      credentials: "same-origin",
      keepalive: true,
      body: JSON.stringify({ attributes: attrs }),
    })
    if (!res.ok) return false
    updateLocalCartAttributes(attrs)
    return true
  }

  async function refreshMiniCart() {
    const cartDrawer = document.querySelector("cart-drawer")
    if (typeof cartDrawer?.refreshFromServer !== "function") return
    await cartDrawer.refreshFromServer()
  }

  function stampWebAuthenticated() {
    return postCartAttributes({
      [AUDIENCE_KEY]: WEB_AUTHENTICATED,
      [AUTHENTICATED_KEY]: AUTHENTICATED_VALUE,
    })
  }

  function clearSessionAttrs() {
    return postCartAttributes({
      [AUDIENCE_KEY]: "",
      [AUTHENTICATED_KEY]: "",
      [CHANNEL_KEY]: "",
      [SOURCE_KEY]: "",
    })
  }

  async function maybeSyncAudience(reason) {
    if (isMobileApp()) return
    if (stampInFlight) return

    const init = window.Shopify?.Cart?.init
    if (typeof init !== "function") return

    stampInFlight = true
    try {
      const cart = await init()
      if (!cart) return

      if (isLoggedIn()) {
        if (
          getCartAudience() === WEB_AUTHENTICATED &&
          isAuthenticatedAttrSet()
        ) {
          return
        }
        const updated = await stampWebAuthenticated()
        if (updated) await refreshMiniCart()
        return
      }

      // Guest: clear leftover audience / app attribution attrs
      if (!hasSessionAttrsToClear()) return
      const updated = await clearSessionAttrs()
      if (updated) await refreshMiniCart()
    } catch (e) {
      console.error("[cart-audience] sync failed", reason, e)
    } finally {
      stampInFlight = false
    }
  }

  function clearAudienceOnLogoutClick() {
    document.addEventListener(
      "click",
      function (event) {
        if (isMobileApp()) return
        let el = event.target
        while (el && el !== document && el.tagName !== "A") {
          el = el.parentNode
        }
        if (!el || el.tagName !== "A") return
        const href = el.getAttribute("href") || ""
        if (href.indexOf("/account/logout") === -1) return

        // Fire-and-forget with keepalive so logout navigation can proceed.
        clearSessionAttrs().catch(function (e) {
          console.error("[cart-audience] logout clear failed", e)
        })
      },
      true,
    )
  }

  function bindInteractionDeferredSync() {
    if (interactionBound) return
    interactionBound = true

    const controller = new AbortController()
    const opts = { capture: true, passive: true, signal: controller.signal }

    function onInteraction() {
      controller.abort()
      maybeSyncAudience("interaction")
    }

    document.addEventListener("pointerdown", onInteraction, opts)
    document.addEventListener("keydown", onInteraction, opts)
    document.addEventListener("touchstart", onInteraction, opts)
  }

  function onReady() {
    if (isMobileApp()) return
    clearAudienceOnLogoutClick()
    bindInteractionDeferredSync()
  }

  if (document.readyState === "complete") {
    onReady()
  } else {
    window.addEventListener("load", onReady, { once: true })
  }
})()
